
// TinyMCE imports
import "tinymce/tinymce"
import "tinymce/themes/silver";
import "tinymce/icons/default";
import 'tinymce/models/dom/model'
import 'tinymce/skins/ui/oxide/skin.css';
// TinyMCE plugin imports
import 'tinymce/plugins/emoticons';
import 'tinymce/plugins/emoticons/js/emojis';
import 'tinymce/plugins/autoresize';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/autolink';

import Editor from '@tinymce/tinymce-vue'
import FormInputTextField from '../FormInputTextField/FormInputTextField.vue'
import AttachmentsEditor from "../AttachmentsEditor/AttachmentsEditor.vue";
import AttachmentsViewer from "../AttachmentsViewer/AttachmentsViewer.vue";
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import debounce from 'foundation/js/debounce'

export default {
    name: "DirectMessageThread",
    components: {
        AttachmentsEditor,
        AttachmentsViewer,
        Editor,
        FormInputTextField
    },
    props: {
        chatId: {
            type: String,
            required: true
        },
        userData: {
            type: Object,
            required: true
        },
        userSearchTake: {
            type: Number,
            required: false,
            default: 15
        }
    },
    setup(props) {
        const store = useStore()
        const newCommentModel = ref('')
        const editCommentModel = ref('')
        const userSearchModel = ref('')
        const userSearchRef = ref(null) 
        const editCommentId = ref('')
        const displayInputError = ref(false)
        const isSubmitting = ref(false)
    
        const newChatUsers = computed(() => store.state.forum.new_chat_users)
        
        const filesToAdd = ref([])
        const attachmentsComponent = ref()
        const chatContentComponent = ref()
        
        const chatId = computed(() => store.state.forum.direct_message_thread_id)
        const chats = computed(() => store.state.forum.direct_message_thread_data.chat)
        watch(chats, () => { 
            setTimeout(() => { 
                scrollToBottom() 
            }, 100) 
        })

        const chatName = computed(() => store.state.forum.direct_message_thread_data.chatName)
        const chatIcon = computed(() => store.state.forum.direct_message_thread_data.chatIcon)
        const listOfUsers = computed(() => {
            const users = [] 
            store.state.forum.add_users_data?.map(user => {
                if (newChatUsers.value.filter(item => item.id === user.id).length === 0) {
                    users.push(user)
                }
            })
            return users
        })
        const showUserSearch = computed(() => store.state.forum.show_user_search)
        const showSpinner = computed(() => store.state.forum.is_submitting)

        const comments = computed(() => [].concat(...chats.value.map(chat => chat.comments)))
        
        const editCommentObj = computed(() => comments.value.find(comment => comment.id === editCommentId.value))
        const editCommentFiles = computed(() => editCommentObj.value.attachments)
        const editCommentFilesToAdd = ref([])
        const editCommentFilesToRemove = ref([])

        const getLabel = (labelKey) => store.getters['labels/getLabel']({labelCollection: "DirectMessageThread", labelKey: labelKey})

        const showCreateNewChatUsersLabel = computed(() => listOfUsers.value.length === 0 && newChatUsers.value.length > 0)

        const showSearchForUser = () => {
            store.commit('forum/set_show_user_search', true)
        }

        const searchForUser = (e) => {
            if (!userSearchRef.value) {
                userSearchRef.value = e.target
            }

            if (userSearchModel.value.length === 0) {
                store.dispatch('forum/clearSearchForUser')
                return
            }

            debounce(function () {
                store.dispatch('forum/searchForUser', { 
                    query: userSearchModel.value,
                    take: props.userSearchTake
                })
            }, 500)()
        }

        const editComment = (commentId, text) => {
            editCommentId.value = commentId
            editCommentModel.value = text
        }

        const saveEditComment = (commentId) => {

            const formData = new FormData()

            if (editCommentModel.value) {
                formData.append('text', editCommentModel.value)
            }

            editCommentFilesToAdd.value.forEach(file => {
                formData.append('filesToAdd', file)
            })
            
            editCommentFilesToRemove.value.forEach(fileId => {
                formData.append('filesToRemove', fileId)
            })

            store.dispatch('forum/editDirectMessageThreadComment', { 
                body: formData, 
                commentId, 
                chatId: chatId.value,
                userId: props.userData.userId
            })
            
            setTimeout(() => { 
                editCommentId.value = ''
            }, 50)
        }

        const cancelEditComment = () => {
            editCommentId.value = ''
        }

        const deleteComment = (e, commentId) => {
            store.dispatch('main/openPageOverlay',{
                overlayContent: 'DeleteCommentOverlay',
                page_overlay_content_data: { commentId: commentId },
                returnTarget: e.currentTarget
            })
            store.commit('main/set_page_overlay_content_data', {
                callback: async () => {
                    if(isSubmitting.value){                
                        return;
                    }          
                    isSubmitting.value = true
                    await store.dispatch('forum/deleteDirectMessageThreadComment', {
                        commentId: commentId,
                        chatId: chatId.value,
                        userId: props.userData.userId
                    })
                    isSubmitting.value = false  
                    setTimeout(() => {
                        store.dispatch('main/closePageOverlay')
                    }, 50)
                }
            })
        }

        const deleteThread = function(e) {            
            store.dispatch('main/openPageOverlay',{
                overlayContent: 'DeletePostOverlay',
                returnTarget: e.currentTarget
            })

            store.commit('main/set_page_overlay_content_data', {
                callback: async () => {
                    if(isSubmitting.value){                
                        return;
                    }          
                    isSubmitting.value = true
                    var success = await store.dispatch('forum/deleteDirectMessageThread', {
                        chatId: chatId.value
                    })
                    if (success) {
                        location.reload()
                    } else {
                        isSubmitting.value = false  
                    }
                }
            })
        }
        
        const addUserToThread = (user) => {
            const existing = newChatUsers.value.filter(item => item.id !== user.id)
            store.commit('forum/set_new_chat_users', [...existing, user])
            store.dispatch('forum/clearSearchForUser')
            userSearchModel.value = ''  
            userSearchRef.value?.focus()
        }

        const handleCreateNewChat = async () => {
            if (newChatUsers.value.length > 0) {
                await store.dispatch('forum/createNewDirectMessageThread', { 
                    userIds: newChatUsers.value.map(user => user.id),
                    message: newCommentModel.value,
                    userId: props.userData.userId,
                    files: filesToAdd.value
                })
            }
        }

        const scrollToBottom = () => {
            if (chatContentComponent.value) {
                chatContentComponent.value.scrollTop = chatContentComponent.value.scrollHeight
            }
        }

        const submitNewComment = async () => {
            if (!newCommentModel.value) {
                displayInputError.value = true
                return
            }

            displayInputError.value = false

            if (!chatId.value) {
                handleCreateNewChat()
            } else {
                await store.dispatch('forum/submitDirectMessageThreadComment', { 
                    text: newCommentModel.value,
                    chatId: chatId.value,
                    userId: props.userData.userId,
                    files: filesToAdd.value
                })
            }

            newCommentModel.value = ''

            attachmentsComponent.value.clear()

            scrollToBottom()
        }

        const getClassModifier = (comment) => {
            let result = ''
            if (comment.userId === props.userData.userId) {
                result += 'kb-direct-message-thread__content-comment--left'
            }
            if (comment.id === editCommentId.value) {
                result += ' kb-direct-message-thread__content-comment--edit'
            }
            return result
        }
        
        return {
            showSpinner,
            showCreateNewChatUsersLabel,
            newChatUsers,
            chats,
            chatName,
            chatIcon,
            newCommentModel,
            editCommentModel,
            userSearchModel,
            editCommentId,
            displayInputError,
            showUserSearch,
            listOfUsers,
            addUserToThread,
            getClassModifier,
            editComment,
            deleteComment,
            deleteThread,
            submitNewComment,
            showSearchForUser,
            searchForUser,
            saveEditComment,
            cancelEditComment,
            getLabel,
            filesToAdd,
            attachmentsComponent,
            chatContentComponent,
            editCommentFiles,
            editCommentFilesToAdd,
            editCommentFilesToRemove
        }
    }
}